import React, { useEffect, useState } from 'react'
import { Controller } from 'react-hook-form'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { useTranslation } from 'react-i18next'

import { useContactForm } from '@/contexts/ContactFormContext'

import { getCountryId, getItemByLang } from '@/services/language'
import { LOCAL_KEYS } from '@/services/browserStorage'
import { ASSET_PATHS, PAGE_PATHS } from '@/services/paths'

import ImageNext from '@/components/ImageNext'
import Input from '../Input'
import Checkbox from '../Checkbox'
import StepButtons from '../StepButtons'
import SelectProfession from '../SelectProfession'

import useStepForm from '../../hooks/useStepForm'
import { useSubmitForm } from '../../hooks/useSubmitForm'

import * as S from './styles'

import type {
  ContactFormStepFourDataType,
  ContactFormStepOneDataType,
  ContactFormStepThreeDataType,
  ContactFormStepTwoDataType,
  ProfessionOptionDataType
} from '@/types/components/ContactForm'

interface StepProps {
  handlePreviousStep: () => void
  handleNextStep: () => void
}

interface StepFourProps extends StepProps {
  setSubmitError: (error: boolean) => void
}

export const FirstStep: React.FC<StepProps> = ({ handleNextStep }) => {
  const { t } = useTranslation()
  const { formData } = useContactForm()

  const {
    register,
    handleSubmit,
    formState: { errors },
    onNextStep,
    watch
  } = useStepForm<ContactFormStepOneDataType>('step1', {
    email: formData.step1?.email ?? '',
    checkbox: formData.step1?.checkbox ?? false
  })

  return (
    <S.FirstStepContainer>
      <h2>{t('contactPage.form.title')}</h2>

      <div className="step-content">
        <div>
          <Input
            label={`${t('contactPage.form.businessEmail')} *`}
            errors={!!errors.email}
            register={register('email', {
              required: t('messages.requiredField'),
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: t('messages.invalidEmail')
              }
            })}
          />
          {errors.email && (
            <S.ErrorMessage>{errors.email.message}</S.ErrorMessage>
          )}
        </div>

        <Checkbox
          label={t('contactPage.form.allowReceiveEmails')}
          id="allow-receive-emails"
          isChecked={watch('checkbox')}
          register={register('checkbox')}
        />
      </div>

      <StepButtons
        currentStep={1}
        handleNextStep={handleSubmit(data => onNextStep(data, handleNextStep))}
      />
    </S.FirstStepContainer>
  )
}

export const SecondStep: React.FC<StepProps> = ({
  handlePreviousStep,
  handleNextStep
}) => {
  const { t } = useTranslation()
  const { formData } = useContactForm()

  const {
    register,
    handleSubmit,
    formState: { errors },
    onNextStep,
    onPreviousStep,
    getValues
  } = useStepForm<ContactFormStepTwoDataType>('step2', {
    fullName: formData.step2?.fullName ?? ''
  })

  return (
    <S.SecondStepContainer>
      <h2>{t('contactPage.form.title')}</h2>
      <div className="step-content">
        <div>
          <Input
            label={`${t('contactPage.form.fullName')} *`}
            errors={!!errors.fullName}
            register={register('fullName', {
              required: t('messages.requiredField')
            })}
          />
          {errors.fullName && (
            <S.ErrorMessage>{errors.fullName.message}</S.ErrorMessage>
          )}
        </div>

        <StepButtons
          currentStep={2}
          handlePreviousStep={() =>
            onPreviousStep(getValues(), handlePreviousStep)
          }
          handleNextStep={handleSubmit(data =>
            onNextStep(data, handleNextStep)
          )}
        />
      </div>
    </S.SecondStepContainer>
  )
}

export const ThirdStep: React.FC<StepProps> = ({
  handlePreviousStep,
  handleNextStep
}) => {
  const { t } = useTranslation()
  const { formData } = useContactForm()
  const [phoneValue, setPhoneValue] = useState(formData.step3?.phone ?? '')

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
    onNextStep,
    onPreviousStep,
    getValues
  } = useStepForm<ContactFormStepThreeDataType>('step3', {
    phone: formData.step3?.phone ?? ''
  })

  const language = localStorage.getItem(LOCAL_KEYS.client_language)

  useEffect(() => {
    register('phone', {
      required: t('messages.requiredField'),
      validate: value => value.trim().length > 0 || t('messages.requiredField')
    })
  }, [register, t])

  return (
    <S.ThirdStepContainer>
      <h2>{t('contactPage.form.title')}</h2>
      <div className="step-content">
        <div className="phone-input">
          <label htmlFor="phone-input">{`${t('contactPage.form.phone')} *`}</label>
          <PhoneInput
            inputProps={{ id: 'phone-input' }}
            inputClass="phone-input-input"
            buttonClass="phone-input-button"
            countryCodeEditable={true}
            country={language ? getCountryId(language).toLowerCase() : 'br'}
            value={phoneValue}
            onChange={value => {
              setPhoneValue(value)
              setValue('phone', value, { shouldValidate: true })
            }}
          />
          {errors.phone && (
            <S.ErrorMessage>{errors.phone.message}</S.ErrorMessage>
          )}
        </div>

        <StepButtons
          currentStep={3}
          handlePreviousStep={() =>
            onPreviousStep(getValues(), handlePreviousStep)
          }
          handleNextStep={handleSubmit(data =>
            onNextStep(data, handleNextStep)
          )}
        />
      </div>
    </S.ThirdStepContainer>
  )
}

export const FourthStep: React.FC<StepFourProps> = ({
  handlePreviousStep,
  handleNextStep,
  setSubmitError
}) => {
  const { t } = useTranslation()
  const { formData } = useContactForm()
  const { submit } = useSubmitForm()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const normalizeToOption = (
    input: string | ProfessionOptionDataType | undefined
  ): ProfessionOptionDataType => {
    if (typeof input === 'string') {
      return { value: input, label: input }
    }
    return input ?? { value: '', label: '' }
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    onNextStep,
    onPreviousStep,
    getValues,
    control
  } = useStepForm<ContactFormStepFourDataType>('step4', {
    institutionName: formData.step4?.institutionName ?? '',
    profession: normalizeToOption(formData.step4?.profession)
  })

  const handleFinalSubmit = async (data: ContactFormStepFourDataType) => {
    if (isSubmitting) return

    setIsSubmitting(true)
    setSubmitError(false)
    onNextStep(data, () => {})

    try {
      await submit({ ...formData, step4: data })
      handleNextStep()
    } catch (e) {
      console.error('Erro ao enviar o formulário:', e)
      setSubmitError(true)
      handleNextStep()
    }
  }

  return (
    <S.FourthStepContainer>
      <h2>{t('contactPage.form.title')}</h2>
      <div className="step-content">
        <div className="step-inputs">
          <div>
            <Input
              label={`${t('contactPage.form.institutionName')} *`}
              errors={!!errors.institutionName}
              register={register('institutionName', {
                required: t('messages.requiredField')
              })}
            />
            {errors.institutionName && (
              <S.ErrorMessage>{errors.institutionName.message}</S.ErrorMessage>
            )}
          </div>
          <div>
            <Controller
              name="profession"
              control={control}
              rules={{
                validate: option =>
                  option.value !== '' || t('messages.requiredField')
              }}
              render={({ field }) => (
                <SelectProfession
                  value={normalizeToOption(field.value)}
                  onChange={field.onChange}
                  error={!!errors.profession}
                />
              )}
            />
            {errors.profession && (
              <S.ErrorMessage>{errors.profession.message}</S.ErrorMessage>
            )}
          </div>
        </div>
        <div className="step-confirmation">
          <StepButtons
            currentStep={4}
            handlePreviousStep={() =>
              onPreviousStep(getValues(), handlePreviousStep)
            }
            handleNextStep={handleSubmit(handleFinalSubmit)}
            isSubmitting={isSubmitting}
          />
          <p className="privacy-notice">
            {t('contactPage.form.privacyPolicy.text')}{' '}
            <a
              href={getItemByLang(
                PAGE_PATHS.privacy_en,
                PAGE_PATHS.privacy_pt,
                PAGE_PATHS.privacy_es
              )}
            >
              {t('contactPage.form.privacyPolicy.link')}
            </a>
          </p>
        </div>
      </div>
    </S.FourthStepContainer>
  )
}

export const FinishStep: React.FC = () => {
  const { t } = useTranslation()

  return (
    <S.FinishStepContainer>
      <ImageNext
        id="success-check"
        src={ASSET_PATHS.animation_check}
        alt="success"
        className="success-check"
      />
      <h2>{t('contactPage.form.success.header')}</h2>
      <p>{t('contactPage.form.success.label')}</p>
    </S.FinishStepContainer>
  )
}

export const ErrorStep: React.FC = () => {
  const { t } = useTranslation()

  return (
    <S.FinishStepContainer>
      <span className="material-icons-sharp error-icon">cancel</span>
      <h2>{t('contactPage.form.error.header')}</h2>
      <p>{t('contactPage.form.error.label')}</p>
    </S.FinishStepContainer>
  )
}
